import React, { useEffect, useState, useMemo } from 'react';
import { useMutation } from 'react-query';
import AxiosUtil from '../../Utils/AxiosUtil';
import parse from 'html-react-parser';
import { useParams } from 'react-router';
import {
    AgGrid,
    Alert as ServaAlert,
    Chart as ServaChart,
    Col,
    DatePicker,
	downloadCsv,
	downloadTabularExcel,
    downloadTabularExcel64,
    formatDate,
    IconBar,
    IconItem,
    Input as ServaInput,
    Modal as ServaModal,
    Option,
    registerAll,
    Row,
    Select,
    Skeleton,
    Title,
    Text,
    WithAsterisk,
} from '@servahealth/serva-ui-kit';
import { Select as AntSelect } from 'antd';
import useAuth from '../../Hooks/useAuth';
import LoginPage from '../../Components/Login/LoginPage';
import Report from '../../Interfaces/Responses/Dashboard/Report';
import ServaDBChart from '../../Interfaces/Responses/Reports/Charts/Chart';
import Input from '../../Interfaces/Responses/Dashboard/Input';
import { FilterInput } from '../../Interfaces/Responses/Dashboard/FilterInput';
import DataSets from '../../Interfaces/Responses/Reports/DataSets/DataSets';
import DataSetDataResponseModel from '../../Interfaces/Responses/Reports/DataSets/DataSetDataResponseModel';
import Dashboard from '../../Constants/Endpoints/Dashboard';
import Reports from '../../Constants/Endpoints/Reports';
import Users from '../../Constants/Endpoints/Users';
import GetDatasetDataUsingIDsV2 from '../../Interfaces/Requests/Reports/GetDatasetDataUsingIDsV2';
import PostChartDataUsingIDsV2 from '../../Interfaces/Requests/Reports/GetChartDataUsingIDsV2';
import GetReportDataForUserByCurrentToken from '../../Interfaces/Requests/Reports/GetReportDataForUserByCurrentToken';
import GetReportFileforUserByCurrentTokenRequest from '../../Interfaces/Requests/Dashboard/GetReportFileforUserrByCurrentToken';
import GetReportFileforUserByCurrentTokenResponse from '../../Interfaces/Responses/Dashboard/GetReportFileforUserByCurrentToken';
import UpdateUserDefaultReportUsingCurrentToken from '../../Interfaces/Requests/Users/UpdateUserDefaultReportUsingCurrentToken';
import '../../Css/Loading.css';
import ChartJSChartData from '../../Interfaces/Responses/Reports/Charts/ChartData';
import { getServaChartOptionsFromServaChartType, transformServaChartToChartJSChart, } from '../../Utils/ChartUtil';
import { ServaChartOptions, ServaChartTypes, } from '../../Interfaces/Chart/Type';
import { ObjectUtil } from '../../Utils/ObjectUtil';
import AutomaticDelivery from './AutomaticDelivery';
import ParsedReportLayout from '../ParsedReportLayout';
import DataSetParameters from '../../Interfaces/DataSet/DataSetParameters';
import ChartParameters from '../../Interfaces/Chart/ChartParameters';
import NoDataParameters from '../../Interfaces/NoData/NoDataParameters';
import ReportItemIDMap from '../../Interfaces/Reports/ItemIdMap';
import InputMap from '../../Interfaces/Reports/InputMap';
import InputMaps from '../../Interfaces/Reports/InputMaps';
import { populateChartInputs, loadPassthroughInputMaps, populateDrilldownDataSetInputs } from '../../Components/ParsedReportLayout/ReportItem/ReportItemHelper';
import { filterInputArrayToValueArray } from '../../Utils/ConverterUtil';
import DebugQuery from '../../Interfaces/Reports/DebugQuery';
import AgGridJSRowData from '../../Interfaces/Responses/Reports/DataSets/DataSetData';
import {dateTimeComparator, isDateColumnFormat, transformServaGridToAgGridJSGrid as transformServaGridDataToAgGridJSData,} from '../../Utils/AgGridUtil';

registerAll();

/**
 * Get HTML renderer for column that may contain HTML
 * @param {*} field 
 * @returns 
 */
const getHTMLRenderer = (params: any) => parse(params.value);

const Home: React.FC = () => {
    const { isAuthenticated, user } = useAuth();
    const { reportId } = useParams();
    const [filterModalVisible, setFilterModalVisible] = useState(false);
    const [gridDrillDownModalVisible, setGridDrillDownModalVisible] = useState(false);
    const [chartDrillDownModalVisible, setChartDrillDownModalVisible] = useState(false);
    const [drillDownTitle, setDrillDownTitle] = useState('');
    const [drillDownDataTitle, setDrillDownDataTitle] = useState('');
    const [filterInputs, setFilterInputs] = useState<FilterInput[]>([]);
    const [inputMaps, setInputMaps] = useState<InputMaps[]>([]);
    const [globalFilterInputs, setGlobalFilterInputs] = useState<FilterInput[]>([]);
    const [allFiltersSet, setAllFiltersSet] = useState(false);
    const [layout, setLayout] = useState('');
    const [modalReportData, setModalReportData] = useState<DataSets>({dataSets: [],});
    const [gridData, setGridData] = useState<AgGridJSRowData>();
    const [okClickedOnFilterModal, setOkClickedOnFilterModal] = useState(false);
    const [report, setReport] = useState<Report>();
    const [errorText, setErrorText] = useState<string>();
    const [debugQueryVisible, setDebugQueryVisible] = useState(false);
    const [debugQuery, setDebugQuery] = useState<DebugQuery>();
    const [drillDownType, setDrillDownType] = useState('');
    const [modalChartData, setModalChartData] = useState<ChartJSChartData>();
    const [automaticDeliveryVisible, setAutomaticDeliveryVisible] = useState(false);
    const [currentPage, setCurrentPage] = useState<number>(0);
    const [itemIdMap, setItemIdMap] = useState<ReportItemIDMap[]>();

    const [currentError, setCurrentError] = useState('');
    
    function callFilters(report: Report) {
        setOkClickedOnFilterModal(false);
        setFilterInputs([]);
        setFilterModalVisible(true);
        loadFilters(report.value ?? report.id.toString());
    }

    useEffect(() => {
        if (!!reportId) {
            setOkClickedOnFilterModal(false);
            setFilterInputs([]);
            setFilterModalVisible(true);
            loadFilters(reportId);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reportId])

    function extractReportPageReportItems(input: string): number[] {
        if (!input) return [];
        return (input.match(/\{(\d+)\}/g) || []).map((s) =>
            Number(s.slice(1, -1))
        );
    }

    const statusBar = useMemo(() => {
        return {
            statusPanels: [
                { statusPanel: 'agTotalAndFilteredRowCountComponent' },
                { statusPanel: 'agTotalRowCountComponent' },
                { statusPanel: 'agFilteredRowCountComponent' },
                { statusPanel: 'agSelectedRowCountComponent' },
                { statusPanel: 'agAggregationComponent' },
            ],
        };
    }, []);

    const iconBarArgsAdmin: any = {
        icons: [
            {
                type: 'csv',
                tooltipText: 'download csv',
                onClick: () =>
                    downloadCsv (
                        modalReportData.dataSets[0]?.columnDefs ?? [],
                        modalReportData.dataSets[0]?.rowData ?? [],
                        (report?.name ?? 'rpt_csv') + '.csv'
                    ),
            },
            {
                type: 'excel',
                tooltipText: 'download excel',
                onClick: () =>
                    downloadTabularExcel (
                        modalReportData.dataSets[0]?.columnDefs ?? [],
                        modalReportData.dataSets[0]?.rowData ?? [],
                        (report?.name ?? 'rpt_excel') + '.xlsx'
                    ),
            },
            {
                type: 'debug',
                tooltipText: 'report debug',
                onClick: () => setDebugQueryVisible(true),
            },
        ],
        orientation: 'horizontal',
        spacing: 30,
    };
    const iconBarArgs: any = {
        icons: [
            {
                type: 'csv',
                tooltipText: 'download csv',
                onClick: () =>
                    downloadCsv (
                        modalReportData.dataSets[0]?.columnDefs ?? [],
                        modalReportData.dataSets[0]?.rowData ?? [],
                        (report?.name ?? 'rpt_csv') + '.csv'
                    ),
            },
            {
                type: 'excel',
                tooltipText: 'download excel',
                onClick: () =>
                    downloadTabularExcel (
                        modalReportData.dataSets[0]?.columnDefs ?? [],
                        modalReportData.dataSets[0]?.rowData ?? [],
                        (report?.name ?? 'rpt_excel') + '.xlsx'
                    ),
            },
        ],
        orientation: 'horizontal',
        spacing: 30,
    };

    useEffect(() => {
        // action on update of drilldown data
        setGridDrillDownModalVisible(modalReportData.dataSets.length > 0);
    }, [modalReportData]);
    
    const hasRequiredFilter = () => {
        if (!filterInputs) {
            return false;
        }
        
        return (filterInputs.filter((eachInput) => eachInput.input.required)?.length > 0);
    };

    const getDefaultGlobalFilterDateValue = (
        uiFilterInput: FilterInput,
        rightOfPipe: boolean
    ) => {
        let ret = '';
        // ---- DEBUG
        // console.log('-----------------------------------------');
        // console.log('---- getDefaultGlobalFilterDateValue ----');
        // console.log('-----------------------------------------');
        // console.log('**** uiFilterInput ****');
        // console.log(uiFilterInput);
        // console.log('**** rightOfPipe ****');
        // console.log(rightOfPipe);
        // console.log('**** globalFilterInputs ****');
        // console.log(globalFilterInputs);
        // ---- END DEBUG
        try {
            if (globalFilterInputs.length > 0) {
                const existingGlobalFilter = globalFilterInputs.find(
                    (eachFilter) => eachFilter.input.globalDesignator === uiFilterInput.input.globalDesignator
                );
                // ---- DEBUG
                // console.log('**** existingGlobalFilter ****');
                // console.log(existingGlobalFilter);
                // ---- END DEBUG
                if ((existingGlobalFilter !== undefined)) {
                    if ((existingGlobalFilter.value !== undefined)) {

                        // check for date type first then if string then split

                        if (existingGlobalFilter.value instanceof Date) {
                            // ---- DEBUG
                            // let x = existingGlobalFilter.value;
                            // console.log('**** existingGlobalFilter Date ****');
                            // console.log(x);
                            // ---- END DEBUG

                            let defaultDate = existingGlobalFilter.value.toLocaleDateString();
                            if ( !defaultDate && uiFilterInput.value && uiFilterInput.value !== '' ) {
                                defaultDate = uiFilterInput.value;
                            }
                            return defaultDate && defaultDate !== '' ? formatDate(defaultDate, 'YYYY-MM-DD') : ret;
                        }
                        else {
                            // ---- DEBUG
                            // let x = existingGlobalFilter.value.split('|');
                            // console.log('**** existingGlobalFilter split() ****');
                            // console.log(x);
                            // ---- END DEBUG
    
                            let defaultDate = rightOfPipe ? existingGlobalFilter.value.split('|')[1] : existingGlobalFilter.value.split('|')[0];
                            if ( !defaultDate && uiFilterInput.value && uiFilterInput.value !== '' ) {
                                defaultDate = uiFilterInput.value;
                            }
                            return defaultDate && defaultDate !== '' ? formatDate(defaultDate, 'YYYY-MM-DD') : ret;
                        }
                    }
                }
            }
        } catch (error: any) {
            console.error('getDefaultGlobalFilterDateValue Error ', error);
        }
        return ret;
    };

    const getDynamicFilters = () => {
        // ---- DEBUG
        // console.log('---------------------------');
        // console.log('---- getDynamicFilters ----');
        // console.log('---------------------------');
        // console.log('**** filterInputs ****');
        // console.log(filterInputs);
        // ---- END DEBUG
        if (filterInputs.length === 0) return <h1>No applicable filters...</h1>;
        return filterInputs.map((filterInput) => {
            return (
                <WithAsterisk
                    key={filterInput.input.id}
                    required={filterInput.input.required}
                >
                    {filterInput.input.dataFormat === 'DateTime' &&
                        filterInput.input.selector !== 'between' && (
                            <DatePicker
                                defaultValue={getDefaultGlobalFilterDateValue(
                                    filterInput,
                                    false
                                )}
                                onChange={(e: any) =>
                                    handleFilterChange(filterInput, e?.$d)
                                }
                                label={filterInput.input.title}
                            />
                        )}
                    {filterInput.input.dataFormat === 'DateTime' &&
                        filterInput.input.selector === 'between' && (
                            <>
                                <div>
                                    <DatePicker
                                        defaultValue={getDefaultGlobalFilterDateValue(
                                            filterInput,
                                            false
                                        )}
                                        onChange={(e: any) =>
                                            handleFilterChange(filterInput, e?.$d)
                                        }
                                        label={filterInput.input.title}
                                    />
                                </div>
                                <div>
                                    <DatePicker
                                        defaultValue={getDefaultGlobalFilterDateValue(
                                            filterInput,
                                            true
                                        )}
                                        onChange={(e: any) =>
                                            handleFilterChange(filterInput, e?.$d, true)
                                        }
                                        label="and"
                                    />
                                </div>
                            </>
                        )}
                    {filterInput.input.dataFormat !== 'DateTime' &&
                        filterInput.input.sourceList.length > 0 && (
                            <Select
                                label={filterInput.input.title}
                                mode={filterInput.input.multiSelect ? "multiple" : undefined}
                                styles={{ popup: { root: { width: 350 } } }}
                                placeholder={filterInput.input.title}
                                onChange={(e: any) => handleFilterChange(filterInput, e)}
                                style={{ minWidth: 200 }}
                            >
                                {filterInput.input.sourceList.map(
                                    (eachSourceList) => {
                                        return (
                                            <Option
                                                value={eachSourceList.value}
                                            >
                                                {eachSourceList.display}
                                            </Option>
                                        );
                                    }
                                )}
                            </Select>
                        )}
                    {filterInput.input.dataFormat !== 'DateTime' &&
                        filterInput.input.sourceList.length === 0 && (
                            <>
                            <label>{filterInput.input.title}</label>
                            <ServaInput
                                onChange={(e: any) => {
                                    handleFilterChange(filterInput, e?.target.value);}
                                }
                            />
                            </>
                        )}
                </WithAsterisk>
            );
        });
    };

    const updateFilterInput = (
        existingFilterInput: FilterInput,
        newFilterInput: FilterInput
    ) => {
        // ---- DEBUG
        // console.log('---------------------------');
        // console.log('---- updateFilterInput ----');
        // console.log('---------------------------');
        // console.log('**** existingFilterInput ****');
        // console.log(existingFilterInput);
        // console.log('**** newFilterInput ****');
        // console.log(newFilterInput);
        // ---- END DEBUG
        const index = globalFilterInputs.findIndex(
            (eachFilter) =>
                eachFilter.input.globalDesignator ===
                existingFilterInput.input.globalDesignator
        );
        if (index !== -1) {
            const updatedFilters = [...globalFilterInputs];
            updatedFilters[index] = newFilterInput;
            // ---- DEBUG
            // console.log('**** updatedFilters ****');
            // console.log(updatedFilters);
            // ---- END DEBUG
        setGlobalFilterInputs(updatedFilters);
        }
        else {
            // $$$-COMPLETE
        }
    };

    function handleFilterChange(
        changedFilterInput: FilterInput,
        changedValue: any,
        rightOfPipe: boolean = false
    ) {
        // ---- DEBUG
        // console.log("----------------------------");
        // console.log("---- handleFilterChange ----");
        // console.log("----------------------------");
        // console.log("**** changedFilterInput ****");
        // console.log(changedFilterInput);
        // console.log("**** changedValue ****");
        // console.log(changedValue);
        // ---- END DEBUG
        setOkClickedOnFilterModal(false);
        let internalGlobalFilterInputs = globalFilterInputs;
        filterInputs.forEach((filterInput) => {
            // ---- DEBUG
            // console.log('**** next filterInput ****');
            // console.log(filterInput);
            // ---- END DEBUG
            if (changedFilterInput.input.id === filterInput.input.id) {
                if (
                    filterInput.input.dataFormat === 'DateTime' &&
                    filterInput.input.selector === 'between'
                ) {
                    if (rightOfPipe) {
                        if (filterInput.hasValue) {
                            const leftOfPipe = filterInput.value
                                .toString()
                                .split('|')[0];
                            filterInput.value = `${leftOfPipe}|${changedValue}`;
                        } else {
                            filterInput.value = changedValue;
                        }
                    } else {
                        let valueRightOfPipe = '';
                        const splitArr = filterInput.value
                            .toString()
                            .split('|');
                        if (splitArr.length === 2) {
                            valueRightOfPipe = splitArr[1];
                        }
                        filterInput.value = `${changedValue}|${valueRightOfPipe}`;
                    }
                } else if (Array.isArray(changedValue)) {
                    filterInput.value = changedValue.join('~~');
                } else {
                    filterInput.value = changedValue;
                }
                if (
                    filterInput.value &&
                    !filterInput.value.toString().includes('undefined')
                ) {
                    filterInput.hasValue = true;
                } else {
                    filterInput.hasValue = false;
                }
            }
            const existingGlobalFilter = internalGlobalFilterInputs.find(
                (eachFilter) =>
                    eachFilter.input.globalDesignator ===
                    filterInput.input.globalDesignator
            );
            if (existingGlobalFilter !== undefined) {
                // console.log('**** defined, so updating filter ****');
                updateFilterInput(existingGlobalFilter, filterInput);
            } else if (filterInput.input.globalDesignator !== '') {
                // console.log('**** undefined, so adding filter ****');
                //setGlobalFilterInputs([...globalFilterInputs, filterInput]);
                let updatedFilters = internalGlobalFilterInputs;
                updatedFilters.push(filterInput);
                // console.log('**** updatedFilters ****');
                // console.log(updatedFilters);
                internalGlobalFilterInputs = updatedFilters;
            }
            // ---- DEBUG
            // console.log('**** globalFilterInputs ****');
            // console.log(globalFilterInputs);
            // ---- END DEBUG
        });

        setGlobalFilterInputs(internalGlobalFilterInputs);

        let allRequiredFiltersHaveValue = true;
        filterInputs.forEach((filterInput) => {
            if (filterInput.input.required && !filterInput.hasValue) {
                allRequiredFiltersHaveValue = false;
            }
        });
        if (allRequiredFiltersHaveValue) {
            setAllFiltersSet(true);
        } else {
            setAllFiltersSet(false);
        }
    }

    function isCurrentReportDefaultReport(reportId: string) {
        return reportId === user?.runningUser?.defaultReportId?.toString();
    }

    // reset report on running user change (impersonation)
    useEffect(() => {
        setLayout('');
        setInputMaps([]);
        setFilterInputs([]);
        setItemIdMap([]);
    }, [user?.runningUser.currentToken]);

    const { mutate: loadFilters, isLoading: isLoadFiltersLoading, isError: isLoadFiltersError, error: getLoadFiltersError, } = useMutation(
        async (reportId: string) => {
            let responseData: Promise<Report>;
            try {
                setCurrentPage(0);
                const url = `${process.env.REACT_APP_API_ROOT}${Dashboard.GET_REPORT_DATA_FOR_USER_BY_CURRENT_TOKEN}`;
                const requestData: GetReportDataForUserByCurrentToken = { grdu_token: user?.runningUser.currentToken ?? '', grdu_reportid: reportId ?? '', };
                responseData = AxiosUtil.get<Report, GetReportDataForUserByCurrentToken>(url, requestData);
                //return responseData;
            } catch (err) {
                setCurrentError('GET_REPORT_DATA_FOR_USER_BY_CURRENT_TOKEN [Get Report Filters] Internal Error: ' + err);
                console.error('GET_REPORT_DATA_FOR_USER_BY_CURRENT_TOKEN [Get Report Filters] Internal Error: ', err);
                responseData = Promise.reject();
            }
            return responseData;
        },
        {
            onError: (err: any) => {
                setCurrentError('GET_REPORT_DATA_FOR_USER_BY_CURRENT_TOKEN [Get Report Filters] Internal Error: ' + err);
                console.error('GET_REPORT_DATA_FOR_USER_BY_CURRENT_TOKEN [Get Report Filters] Internal Error: ', err);
            },
            onSuccess: (response: Report) => {
                // ---- DEBUG
                // console.log('GET_REPORT_DATA_FOR_USER_BY_CURRENT_TOKEN')
                // console.log(response);
                // ---- END DEBUG
                setReport(response);
                const responseInputs: Input[] = response?.inputs ?? [];
                const inputs: FilterInput[] = [];
                let hasAtLeastOneRequiredFilter = false;
                const inputMaps: InputMaps[] = [];
                response.pages[0].elements.forEach((eachElement) => {
                    const inputMapArray: InputMap[] = [];
                    eachElement.inputMaps.forEach((eachInputMap) => {
                        inputMapArray.push(eachInputMap);
                    });
                    inputMaps.push({
                        objectId: eachElement.objectID,
                        inputMaps: inputMapArray,
                    });
                });
                setInputMaps(inputMaps);
                let i = 0;
                responseInputs.forEach((input) => {
                    let newInput: FilterInput = {
                        key: i,
                        hasValue: false,
                        input: input,
                        value: '',
                    };
                    ++i;
                    const foundFilterInputArr = filterInputs.filter(
                        (eachFilterInput) =>
                            eachFilterInput.input.id === input.id
                    );
                    if (foundFilterInputArr.length > 0) {
                        newInput = foundFilterInputArr[0];
                    }
                    if (input.globalDesignator !== '') {
                        const foundGlobalFilter = globalFilterInputs.find(
                            (eachGlobalInput) =>
                                eachGlobalInput.hasValue &&
                                eachGlobalInput.input.globalDesignator ===
                                    input.globalDesignator
                        );
                        if (foundGlobalFilter && foundGlobalFilter !== null) {
                            newInput.hasValue = true;
                            newInput.value = foundGlobalFilter?.value ?? '';
                        }
                    }
                    if (input.required && !newInput.hasValue) {
                        hasAtLeastOneRequiredFilter = true;
                    }
                    inputs.push(newInput);
                });
                setFilterInputs(inputs);
                const reportLayout = response?.pages[0]?.layout;
                setLayout(reportLayout);
                if (responseInputs.length > 0) {
                    setFilterModalVisible(true);
                } else {
                    setFilterModalVisible(false);
                    setOkClickedOnFilterModal(true);
                    applyFiltersToDataSets(true, reportLayout, response, 0);
                }
                if (hasAtLeastOneRequiredFilter) {
                    setAllFiltersSet(false);
                } else {
                    setAllFiltersSet(true);
                }
            },
        }
    );

    const handleRowClick = (
        dataSetIndex: number,
        index: number,
        rowData: any,
        gridData: AgGridJSRowData
    ) => {
        // ---- DEBUG
        // console.log("**** rowData HRC ****");
        // console.log(rowData);
        // console.log("**** gridData HRC ****");
        // console.log(gridData);
        // console.log("**** DSI HRC ****");
        // console.log(dataSetIndex);
        // console.log("**** I HRC ****");
        // console.log(index);
        // ---- END DEBUG

        const dataSetId = gridData.dataSets[dataSetIndex].drilldownDataSetInputId ?? 0;
        const chartId = gridData.dataSets[dataSetIndex].drilldownDataSetChartId ?? 0;
        if ((dataSetId === 0 && chartId === 0) || (gridData.rawVals[index] === '')) {
            return;
        }
        if (dataSetId > 0) {
            let clnDrilldowns = structuredClone(gridData.dataSets[dataSetIndex].drilldowns);
            // ---- DEBUG
            // console.log(clnDrilldowns);
            // ---- END DEBUG

            const dataSetParameters: DataSetParameters = {
                reportId: report?.id ?? 0,
                reportName: report?.name ?? '',
                reportPageId: 0,
                reportItemId: 0,
                dataSetId: dataSetId,
                tenantId: gridData.dataSets[dataSetIndex].drilldownTenantId ?? 0,
                isDrillDown: true,
                drilldownData: gridData.rawVals[index] ?? '',
                drilldowns: clnDrilldowns,
            };

            // ---- DEBUG
            // console.log('**** DSP ****');
            // console.log(dataSetParameters);
            // ---- END DEBUG
            
            loadDrilldownDataSet(dataSetParameters);
            setDrillDownType('grid');
            setDrillDownDataTitle(gridData.title ?? '');
            setDrillDownTitle(gridData.rawVals[index] ?? '');
        } else {
            let clnDrilldowns = structuredClone(gridData.dataSets[dataSetIndex].drilldowns);
            // ---- DEBUG
            // console.log(clnDrilldowns);
            // ---- END DEBUG
            
            const chartParameters: ChartParameters = {
                reportId: report?.id ?? 0,
                reportName: report?.name ?? '',
                reportPageId: 0,
                reportItemId: 0,
                chartId: chartId,
                dataSetId: 0,
                tenantId: gridData.dataSets[dataSetIndex].drilldownTenantId ?? 0,
                isDrillDown: true,
                drilldownData: gridData.rawVals[index] ?? '',
                drilldowns: clnDrilldowns,
            };

            // ---- DEBUG
            // console.log('**** CP ****');
            // console.log(chartParameters);
            // ---- END DEBUG

            loadDrilldownChart(chartParameters);
            setDrillDownType('chart');
        }
    };

    const handleBarClick = (
        dataSetIndex: number,
        index: number,
        chartData: ChartJSChartData
    ) => {
        const dataSetId =
            chartData.datasets[dataSetIndex].drilldownDataSetInputId ?? 0;
        const chartId =
            chartData.datasets[dataSetIndex].drilldownDataSetChartId ?? 0;
        if (dataSetId === 0 && chartId === 0) {
            return;
        }
        if (dataSetId > 0) {
            let clnDrilldowns = structuredClone(chartData.datasets[dataSetIndex].drilldowns);
            // ---- DEBUG
            // console.log(clnDrilldowns);
            // ---- END DEBUG

            const dataSetParameters: DataSetParameters = {
                reportId: report?.id ?? 0,
                reportName: report?.name ?? '',
                reportPageId: 0,
                reportItemId: 0,
                dataSetId: dataSetId,
                tenantId: chartData.datasets[dataSetIndex].drilldownTenantId ?? 0,
                isDrillDown: true,
                drilldownData: chartData.rawVals[index] ?? '',
                drilldowns: clnDrilldowns,
            };
            loadDrilldownDataSet(dataSetParameters);
            setDrillDownType('grid');
            setDrillDownDataTitle(chartData.title ?? '');
            setDrillDownTitle(chartData.rawVals[index] ?? '');
        } else {
            let clnDrilldowns = structuredClone(chartData.datasets[dataSetIndex].drilldowns);
            // ---- DEBUG
            // console.log(clnDrilldowns);
            // ---- END DEBUG
            
            const chartParameters: ChartParameters = {
                reportId: report?.id ?? 0,
                reportName: report?.name ?? '',
                reportPageId: 0,
                reportItemId: 0,
                chartId: chartId,
                dataSetId: 0,
                tenantId: chartData.datasets[dataSetIndex].drilldownTenantId ?? 0,
                isDrillDown: true,
                drilldownData: chartData.rawVals[index] ?? '',
                drilldowns: clnDrilldowns,
            };
            loadDrilldownChart(chartParameters);
            setDrillDownType('chart');
        }
    };

    const closeGridDrillDownModal = () => {
        setModalReportData({ dataSets: [], });
    };

    const closeChartDrillDownModal = () => {
        setChartDrillDownModalVisible(false);
        setModalChartData(undefined);
    };

    const { mutate: loadDrilldownDataSet, isLoading:isLoadDrilldownDataSetLoading, isError: isLoadDrilldownDataSetError, error: getIsLoadDrilldownDataSetError, } = useMutation(
        async (dataSetParameters: DataSetParameters) => {
            let responseData: Promise<DataSetDataResponseModel>;
            try {
                const url = `${process.env.REACT_APP_API_ROOT}${Reports.GET_DATASET_DATA_USING_IDS_V2}`;
                const passthroughInputMaps = loadPassthroughInputMaps(itemIdMap ? itemIdMap[dataSetParameters.reportItemId] : undefined, dataSetParameters);
                const inputs = populateDrilldownDataSetInputs(passthroughInputMaps);
                const requestData: GetDatasetDataUsingIDsV2 = {
                    currentToken: user?.runningUser.currentToken ?? '',
                    datasetID: dataSetParameters?.dataSetId ?? -1,
                    tenantID: dataSetParameters?.tenantId.toString() ?? '',
                    reportItemID: dataSetParameters.reportItemId,
                    inputs: inputs,
                };
                responseData = AxiosUtil.post<DataSetDataResponseModel, GetDatasetDataUsingIDsV2>(url, requestData);
                //return responseData;
            } catch (err) {
                setCurrentError('GET_DATASET_DATA_USING_IDS_V2 [Load Drilldown Dataset] Internal Error: ' + err);
                console.error('GET_DATASET_DATA_USING_IDS_V2 [Load Drilldown Dataset] Internal Error: ', err);
                responseData = Promise.reject();
            }
            return responseData;
        },
        {
            onError: (err: any) => {
                setCurrentError('GET_DATASET_DATA_USING_IDS_V2 [Load Drilldown Dataset] Internal Error: ' + err);
                console.error('GET_DATASET_DATA_USING_IDS_V2 [Load Drilldown Dataset] Internal Error: ', err);
            },
            onSuccess: (response: DataSetDataResponseModel, dataSetParameters: DataSetParameters) => {
                // ---- DEBUG
                // console.log('GET_DATASET_DATA_USING_IDS_V2');
                // console.log(response);
                // ---- END DEBUG

                const { columns, data, columnDefinitions, datasetColumnFormats } = response;

                // ---- DEBUG
                // console.log('**** columns ****');
				// console.log(columns);
                // console.log('**** data ****');
				// console.log(data);
                // console.log('**** columnDefinitions ****');
				// console.log(columnDefinitions);
                // console.log('**** datasetAggregates ****');
				// console.log(datasetAggregates);
                // console.log('**** datasetColumnFormats ****');
				// console.log(datasetColumnFormats);
                // ---- END DEBUG

                const hiddenTargets = columnDefinitions.filter((column) => !column.visible).map((column) => column.targets);

                const gridData = transformServaGridDataToAgGridJSData(
                    response,
                    report?.name ?? ''
                );

                const rowData = data.map((rowData) => {
                    const rowDataObj: { [key: string]: any } = {};
                    columns.forEach((column, index) => {
                        rowDataObj[column.name] = ObjectUtil.IsEmptyObject(rowData[index]) ? '' : rowData[index];
                    });
                    return rowDataObj;
                })

                setGridData(gridData);

				setDebugQuery({
					queryReportId: dataSetParameters.reportId,
					queryReportName: response.name,
					queryReportPageId: dataSetParameters.reportPageId,
					queryReportItemId: dataSetParameters.reportItemId,
					queryChartId: 0,
					queryDataSetId: dataSetParameters.dataSetId,
					queryString: response.queryDebug,
				});

                hiddenTargets.forEach((index) => {
                    if (columns[index]) {
                        columns[index].hidden = true;
                    }
                });
                if (dataSetParameters.isDrillDown) {
                    setModalReportData({
                        dataSets: [
                            {
                                columnDefs: columns.map((column) => ({
                                    autoHeaderHeight: true,
                                    cellStyle: {textAlign: column.alignment},
                                    comparator: isDateColumnFormat(column.name, datasetColumnFormats) ? dateTimeComparator : undefined,
                                    field: column.name,
                                    filter: true,
                                    headerName: column.title,
                                    hide: column.hidden,
                                    sortable: true,
                                    wrapHeaderText: true,
                                    cellRenderer: datasetColumnFormats.some((x: any) => x.columnName === column.name && x.allowHtml) ? (params: any) => getHTMLRenderer(params) : undefined,
                                })),
                                id: dataSetParameters.reportItemId,
                                rowData: rowData,
                                /*rowData: data.map((rowData) => {
                                    const rowDataObj: { [key: string]: any } = {};
                                    columns.forEach((column, index) => {
                                        rowDataObj[column.name] = ObjectUtil.IsEmptyObject(rowData[index]) ? '' : rowData[index];
                                    });
                                    return rowDataObj;
                                }),*/
                                singleDataPointValue: response.singleDataPointResult,
                                type: response.singleDataPoint ? 'singleDataPoint' : 'grid',
                            },
                        ],
                    });
                }
            },
        }
    );

    const { mutate: loadDrilldownChart, isLoading: isLoadDrilldownChartLoading, isError: isLoadDrilldownChartError, error: getLoadDrilldownChartError, } = useMutation(
        async (chartParameters: ChartParameters) => {
            let responseData: Promise<ServaDBChart>;
            try {
                const url = `${process.env.REACT_APP_API_ROOT}${Reports.GET_CHART_DATA_USING_IDS_V2}`;
                const inputs = populateChartInputs(chartParameters, inputMaps, filterInputs);
                const requestData: PostChartDataUsingIDsV2 = {
                    currentToken: user?.runningUser.currentToken ?? '',
                    chartID: chartParameters?.chartId ?? -1,
                    tenantID: chartParameters?.tenantId ?? -1,
                    inputs: inputs,
                };
                responseData = AxiosUtil.post<ServaDBChart, PostChartDataUsingIDsV2>(url, requestData);
                //return responseData;
            } catch (err) {
                setCurrentError('GET_CHART_DATA_USING_IDS_V2 [Load Drilldown Chart] Internal Error: ' + err);
                console.error('GET_CHART_DATA_USING_IDS_V2 [Load Drilldown Chart] Internal Error: ', err);
                responseData = Promise.reject();
            }
            return responseData;
        },
        {
            onError: (err: any) => {
                setCurrentError('GET_CHART_DATA_USING_IDS_V2 [Load Drilldown Chart] Internal Error: ' + err);
                console.error('GET_CHART_DATA_USING_IDS_V2 [Load Drilldown Chart] Internal Error: ', err);
            },
            onSuccess: (response: ServaDBChart, chartParameters: ChartParameters) => {
                // console.log('GET_CHART_DATA_USING_IDS_V2');
                // ---- DEBUG
                // console.log(response);
                // ---- END DEBUG

                const servaChartOptions: ServaChartOptions =
                    getServaChartOptionsFromServaChartType(
                        response.dataSets[0].reportChartType
                    );
                setDebugQuery({
                        queryReportId: chartParameters.reportId,
                        queryReportName: chartParameters.reportName,
                        queryReportPageId: chartParameters.reportPageId,
                        queryReportItemId: chartParameters.reportItemId,
                        queryChartId: chartParameters.chartId,
                        queryDataSetId: chartParameters.dataSetId,
                        queryString: response.queryDebug
                });
        
                if (servaChartOptions.type) {
                    const chartData = transformServaChartToChartJSChart(
                        response,
                        servaChartOptions,
                        response.title
                    );

                    setChartDrillDownModalVisible(true);
                    setModalChartData(chartData);
                }
            },
        }
    );

    const { mutate: downloadReport, isLoading: isDownloadReportLoading, isError: isDownloadReportError, error: getIsDownloadReportError, } = useMutation(
        async () => {
            let responseData: Promise<GetReportFileforUserByCurrentTokenResponse>;
            try {
                const inputIds = filterInputs.map((eachInput) => eachInput.input.id.toString());
                const url = `${process.env.REACT_APP_API_ROOT}${Dashboard.GET_REPORT_FILE_FOR_USER_BY_CURRENT_TOKEN}`;
                const requestData: GetReportFileforUserByCurrentTokenRequest = {
                    grf_token: user?.runningUser.currentToken ?? '',
                    grf_reportid: report?.id?.toString() ?? '',
                    grf_frtype: '0',
                    grf_inputids: inputIds,
                    grf_inputvals: filterInputArrayToValueArray(filterInputs),
                };
                responseData = AxiosUtil.get<GetReportFileforUserByCurrentTokenResponse, GetReportFileforUserByCurrentTokenRequest>(url, requestData);
                //return responseData;
            } catch (err) {
                setCurrentError('GET_REPORT_FILE_FOR_USER_BY_CURRENT_TOKEN [Download Report] Internal Error: ' + err);
                console.error('GET_REPORT_FILE_FOR_USER_BY_CURRENT_TOKEN [Download Report] Internal Error: ', err);
                responseData = Promise.reject();
            }
            return responseData;
        },
        {
            onError: (err: any) => {
                setCurrentError('GET_REPORT_FILE_FOR_USER_BY_CURRENT_TOKEN [Download Report] Internal Error: ' + err);
                console.error('GET_REPORT_FILE_FOR_USER_BY_CURRENT_TOKEN [Download Report] Internal Error: ', err);
            },
            onSuccess: (response: GetReportFileforUserByCurrentTokenResponse) => {
                downloadTabularExcel64(response.file, response.name);
            },
        }
    );

    const { mutate: manageDefaultReport, isLoading: isManageDefaultReportLoading, isError: isManageDefaultReportError, error: getManageDefaultReportError, } = useMutation(
        async () => {
            let responseData: string;
            try {
                const url = `${process.env.REACT_APP_API_ROOT}${Users.UPDATE_USER_DEFAULT_REPORT_USING_CURRENT_TOKEN}`;
                const requestData: UpdateUserDefaultReportUsingCurrentToken = {
                    currentToken: user?.runningUser.currentToken ?? '',
                    reportID: isCurrentReportDefaultReport(report?.id.toString() ?? '') ? '0' : report?.id.toString() ?? '',
                };
                responseData = await AxiosUtil.post<string, UpdateUserDefaultReportUsingCurrentToken>(url, requestData);
            } catch (err) {
                setCurrentError('UPDATE_USER_DEFAULT_REPORT_USING_CURRENT_TOKEN [Set Default Report] Internal Error: ' + err);
                console.error('UPDATE_USER_DEFAULT_REPORT_USING_CURRENT_TOKEN [Set Default Report] Internal Error: ', err);
                return Promise.reject(err);
            }
            return responseData;
        },
        {
            onError: (err: any) => {
                setCurrentError('UPDATE_USER_DEFAULT_REPORT_USING_CURRENT_TOKEN [Set Default Report] Internal Error: ' + err);
                console.error('UPDATE_USER_DEFAULT_REPORT_USING_CURRENT_TOKEN [Set Default Report] Internal Error: ', err);
            },
            onSuccess: (response: string) => {
                ServaModal.success({
                    title: 'Default Report',
                    content: 'Default report was updated successfully.',
                });

                if (user) {
                    const newDefaultReportId = isCurrentReportDefaultReport(report?.id.toString() ?? '')
                        ? 0
                        : report?.id ?? 0;

                    user.runningUser.defaultReportId = newDefaultReportId;

                    if (user.id === user.runningUser.id) {
                        user.defaultReportId = newDefaultReportId;
                    }
                }
            },
        }
    );

    const handleFilterModalOk = () => {
        setOkClickedOnFilterModal(true);
        setFilterModalVisible(!applyFiltersToDataSets(allFiltersSet, layout, report, currentPage));
    };

    const applyFiltersToDataSets = (
        isAllFiltersSet: boolean,
        reportLayout: string,
        theReport: Report | undefined,
        pageNumber: number
    ) => {
        if (!isAllFiltersSet) {
            setErrorText('Please specify all required filters before continuing');
            return false;
        }
        setErrorText('');

        let reportPageReportItemIDs: number[] = extractReportPageReportItems(reportLayout);

        if (reportPageReportItemIDs.length === 0) {
            reportPageReportItemIDs = [-1];
        }

        const fullPage = reportPageReportItemIDs.length === 1;
        
        let _reportItemIDs: ReportItemIDMap[] = [];

        reportPageReportItemIDs.forEach((reportItemID) => {
            const reportPageElement = theReport?.pages[pageNumber].elements.find((element) => element.id === reportItemID);

            switch (reportPageElement?.type) {
                case 0:
                    const dataSetParameters: DataSetParameters = {
                        reportId: theReport?.id ?? 0,
                        reportName: theReport?.name ?? '',
                        reportPageId: theReport?.pages[pageNumber].id ?? 0,
                        reportItemId: reportItemID,
                        dataSetId: reportPageElement?.underlyingDatasetID ?? 0,
                        tenantId: reportPageElement?.tenantID ?? 0,
                        isDrillDown: false,
                    };

                    _reportItemIDs.push({
                        id: reportItemID,
                        type: 'grid',
                        dataSetParameters,
                        filterInputs,
                        inputMaps,
                        fullPage,
                    });
                    break;

                case 1:
                    const chartParameters: ChartParameters = {
                        reportId: theReport?.id ?? 0,
                        reportName: theReport?.name ?? '',
                        reportPageId: theReport?.pages[pageNumber].id ?? 0,
                        reportItemId: reportItemID,
                        chartId: reportPageElement.objectID,
                        dataSetId: reportPageElement?.underlyingDatasetID ?? 0,
                        tenantId: reportPageElement?.tenantID ?? 0,
                        isDrillDown: false,
                    };

                    _reportItemIDs.push({
                        id: reportItemID,
                        type: 'chart',
                        chartParameters,
                        filterInputs,
                        inputMaps,
                        fullPage,
                    });
                    break;

                default:
                    const noDataParameters: NoDataParameters = {
                        reportId: theReport?.id ?? 0,
                        reportName: theReport?.name ?? '',
                        reportPageId: theReport?.pages[pageNumber].id ?? 0,
                        reportItemId: reportItemID,
                        tenantId: reportPageElement?.tenantID ?? 0,
                        isDrillDown: false,
                    };

                    _reportItemIDs.push({
                        id: reportItemID,
                        type: 'noData',
                        noDataParameters,
                        filterInputs,
                        inputMaps,
                        fullPage,
                    });
                    break;
            }
        });
        setItemIdMap(_reportItemIDs);
        return true;
    };

    const handleReportRefresh = () => {
        if (report === undefined) {
            setCurrentError('Refresh Report: This report is not defined. Please contact application support.');
            return;
        }

        setCurrentError('');
        callFilters(report);
    };

    const handleReportDownload = () => {
        if (report === undefined) {
            setCurrentError('Download Report: This report is not defined. Please contact application support.');
            return;
        }

        if (!report.exportExcel) {
            setCurrentError('Download Report: This report does not support Excel export.');
            return;
        }

        setCurrentError('');
        downloadReport();
    };

    const handleManageDefaultReport = () => {
        if (report === undefined) {
            setCurrentError('Default Report: This report is not defined. Please contact application support.');
            return;
        }

        setCurrentError('');
        manageDefaultReport();
    };

    const handleAutomaticDelivery = () => {
        if (report === undefined) {
            setCurrentError('Automatic Delivery: This report is not defined. Please contact application support.');
            return;
        }

        setCurrentError('');
        setAutomaticDeliveryVisible(true);
    };

    const loadIcons = (): IconItem[] => {
        const icons: IconItem[] = [
            {
                type: 'refresh',
                tooltipText: 'refresh report',
                onClick: () => handleReportRefresh(),
            },
            {
                type: 'filter',
                tooltipText: 'filter report',
                onClick: () =>
                    report && report.inputs && report.inputs.length > 0
                        ? callFilters(report)
                        : setFilterModalVisible(false),
            },
            {
                type: 'download',
                tooltipText: 'download report',
                onClick: () => handleReportDownload(),
            },
            {
                type: 'favorite',
                tooltipText: isCurrentReportDefaultReport(
                    report?.id.toString() ?? ''
                )
                    ? 'clear default report'
                    : 'set as default report',
                onClick: () => handleManageDefaultReport(),
            },
        ];
        if (!hasRequiredFilter()) {
            icons.push({
                type: 'schedule',
                tooltipText: 'Automatic Delivery',
                onClick: () => handleAutomaticDelivery(),
            });
        }
        return icons;
    };

    const handlePageChange = (index: number) => {
        setCurrentPage(index);
        if (report) {
            setLayout(report.pages[index].layout);
            applyFiltersToDataSets(
                true,
                report.pages[index].layout,
                report,
                index
            );
        }
    };

    const pageDropdown = () => {
        if (report && report.pages.length > 1) {
            return (
                <>
                    Current Page:&nbsp;
                    <AntSelect
                        className='m-0 p-0'
                        style={{ maxWidth: 180 }}
                        value={report.pages[currentPage].name}
                        onChange={(value: any, option: any) =>
                            handlePageChange(option.key)
                        }
                    >
                        {report.pages.map((page, index) => (
                            <Option key={index} value={page.name}>
                                {page.name}
                            </Option>
                        ))}
                    </AntSelect>
                </>
            );
        }
    };

    /**
     * This state/effect handles the icon bar background change on scroll.
     */
    const [showBackground, setShowBackground] = useState(false);   
    useEffect(() => {   
        const changeBackground = () => {
            if (window.scrollY > 20) {
                setShowBackground(true);
            } else {
                setShowBackground(false);
            }
        }
        window.addEventListener('scroll', changeBackground);
        return () => { window.removeEventListener('scroll', changeBackground); };
    }, []);

    const buildUserFirstName = () => {
        return (
            <>
                {user?.firstName}{' '}
                {user?.runningUser.firstName !== user?.firstName
                    ? '(running as ' +
                      (user?.runningUser ? user?.runningUser.firstName : '') +
                      ')'
                    : ''}
            </>
        );
    };

    const isAreaLoading = isLoadFiltersLoading || isDownloadReportLoading || isManageDefaultReportLoading;

    if (!isAuthenticated)
        return (
            <>
                <LoginPage></LoginPage>
            </>
        );

    const determineWhichError = () => {
        if ( isLoadFiltersError ) {
            return currentError + ' ' + (getLoadFiltersError as any).response?.data.message;
        }
        else if ( isLoadDrilldownDataSetError ) {
            return currentError + ' ' + (getIsLoadDrilldownDataSetError as any).response?.data.message;
        }
        else if ( isLoadDrilldownChartError ) {
            return currentError + ' ' + (getLoadDrilldownChartError as any).response?.data.message;
        }
        else if ( isDownloadReportError ) {
            return currentError + ' ' + (getIsDownloadReportError as any).response?.data.message;
        }
        else if ( isManageDefaultReportError ) {
            return currentError + ' ' + (getManageDefaultReportError as any).response?.data.message;
        }
        else {
            return currentError;
        }
    }

    return (
        <>
            <ServaModal
                open={
                    filterModalVisible &&
                    !isLoadFiltersLoading &&
                    !isLoadDrilldownDataSetLoading &&
                    !isLoadDrilldownChartLoading &&
                    !isDownloadReportLoading &&
                    !isManageDefaultReportError
                }
                title={'Filters'}
                onOk={handleFilterModalOk}
                onCancel={() => setFilterModalVisible(false)}
                okText="Apply Filters"
            >
                {filterInputs.length > 0 && getDynamicFilters()}
                {errorText && <div style={{ color: 'red' }}>{errorText}</div>}
            </ServaModal>
            <ServaModal
                open={gridDrillDownModalVisible}
                title={drillDownDataTitle + ': Detail Records'}
                height={'600px'}
                width={'600px'}
                destroyOnHidden={true}
                onOk={() => closeGridDrillDownModal()}
                onCancel={() => closeGridDrillDownModal()}
                cancelButtonProps={{ style: { display: 'none' }}}
            >
                {drillDownType === 'grid' && (
                    <div
                        className="report-item"
                        style={{
                            height: '95%',
                            position: 'relative',
                        }}
                    >
                        <div
                            style={{
                                border: '1px solid #000080',
                                height: '50px',
                            }}
                        >
                            <div
                                style={{
                                    fontWeight: 'bold',
                                    textAlign: 'center',
                                    width: '100%',
                                    height: '25px',
                                    backgroundColor: '#B0C4DE',
                                    color: '#000080',
                                }}
                            >
                                <span>{drillDownTitle}</span>
                            </div>
                            <div className="mb-1"></div>
                            {user?.runningUser.isSecurityAdmin ?
                                <div style={{ position: 'absolute' }}><IconBar size="small" {...iconBarArgsAdmin} /></div> : 
                                <div style={{ position: 'absolute' }}><IconBar size="small" {...iconBarArgs} /></div>}
                            <div className="mb-1"></div>
                        </div>
                        <div style={{ height: '550px', width: '550px' }}>
                            <AgGrid
                                columnDefs={ modalReportData?.dataSets.length > 0 ? modalReportData?.dataSets[0].columnDefs : undefined }
                                gridData={ gridData }
                                rowData={ modalReportData?.dataSets.length > 0 ? modalReportData?.dataSets[0].rowData : undefined }
                                statusBar={ statusBar }
                            />
                        </div>
                    </div>
                )}
            </ServaModal>
            <ServaModal
                open={chartDrillDownModalVisible}
                title={modalChartData?.title}
                onOk={() => closeChartDrillDownModal()}
                onCancel={() => closeChartDrillDownModal()}
            >
                {drillDownType === 'chart' && modalChartData && (
                    <ServaChart
                        type={ServaChartTypes[ServaChartTypes.pie]}
                        showLegend={true}
                        data={modalChartData}
                    />
                )}
            </ServaModal>
            <ServaModal
                hideButtons={true}
                open={debugQueryVisible}
                styles={{body: {height: '65vh', overflowY: 'auto'}}}
                title={<b><u>Administrative Debug Information</u></b>}
                width={'800px'}
                onCancel={() => setDebugQueryVisible(false)}
            >
                <Row>
                    <Col span={8}>
                        <label><b><i>Report ID:</i></b></label>
                    </Col>
                    <Col span={16}>
                        <label><b><i>Report Name:</i></b></label>
                    </Col>
                </Row>
                <Row>
                    <Col span={1}>
                    </Col>
                    <Col span={7}>
                        <label>{debugQuery?.queryReportId}</label>
                    </Col>
                    <Col span={1}>
                    </Col>
                    <Col span={15}>
                        <label>{debugQuery?.queryReportName}</label>
                    </Col>
                </Row>
                <Row>
                    <Col span={8}>
                        <label><b><i>Report Page ID:</i></b></label>
                    </Col>
                    <Col span={16}>
                        <label><b><i>Report Item ID:</i></b></label>
                    </Col>
                </Row>
                <Row>
                    <Col span={1}>
                    </Col>
                    <Col span={7}>
                        <label>{debugQuery?.queryReportPageId}</label>
                    </Col>
                    <Col span={1}>
                    </Col>
                    <Col span={15}>
                        <label>{debugQuery?.queryReportItemId}</label>
                    </Col>
                </Row>
                <Row>
                    <Col span={8}>
                        <label><b><i>DataSet ID:</i></b></label>
                    </Col>
                    <Col span={16}>
                        <label><b><i>Chart ID:</i></b></label>
                    </Col>
                </Row>
                <Row>
                    <Col span={1}>
                    </Col>
                    <Col span={7}>
                        <label>{debugQuery?.queryDataSetId === 0 ? 'N/A' : debugQuery?.queryDataSetId}</label>
                    </Col>
                    <Col span={1}>
                    </Col>
                    <Col span={15}>
                        <label>{debugQuery?.queryChartId === 0 ? 'N/A' : debugQuery?.queryChartId}</label>
                    </Col>
                </Row>
                <Row>
                    <Col span={24}>
                        <label><b><i>DataSet Query:</i></b></label>
                    </Col>
                </Row>
                <Row>
                    <Col span={24}>
                        <pre>{parse(debugQuery?.queryString ?? '')}</pre>
                    </Col>
                </Row>
            </ServaModal>
            <ServaModal
                destroyOnHidden={true}
                hideButtons={true}
                open={automaticDeliveryVisible}
                onCancel={() => setAutomaticDeliveryVisible(false)}
            >
                <AutomaticDelivery
                    report={report}
                    close={() => setAutomaticDeliveryVisible(false)}
                ></AutomaticDelivery>
            </ServaModal>
            {
                itemIdMap && (itemIdMap?.length > 0)
                ?
                (
                    <div
                        className='ReportTitleBadge'
                        style={{
                            alignItems: 'center',
                            display: 'flex',
                            justifyContent: 'center',
                            pointerEvents: 'none',
                            zIndex: 11,
                        }}
                    >
                        <span
                            style={{
                                background: 'white',
                                borderRadius: 15,
                                fontSize: 16,
                                fontWeight: 'bold',
                                maxWidth: '100%',
                                overflow: 'hidden',
                                padding: '5px 16px',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {report?.groupName} {report?.name}
                        </span>
                    </div>
                )
                :
                null
            }
            {
                itemIdMap && (itemIdMap?.length > 0)
                ?
                (
                    <div
                        className='ReportActionBar'
                        style={{
                            alignItems: 'center',
                            display: 'flex',
                            padding: 5,
                            zIndex: 10,
                            background: showBackground ? 'white' : 'transparent',
                            backdropFilter: showBackground ? 'blur(10px)' : 'none',
                            boxShadow: showBackground ? '0 2px 4px rgba(0,0,0,0.1)' : 'none',
                            borderRadius: 15,
                            width: pageDropdown() ? undefined : (!hasRequiredFilter() ? '150px' : '120px')
                        }}
                    >
                        <IconBar
                            orientation={'horizontal'}
                            spacing={15}
                            icons={loadIcons()}
                            size="small"
                        />
                        <div style={{ marginLeft: 'auto' }}>
                            {pageDropdown()}
                        </div>
                    </div>
                )
                :
                (
                    !isAreaLoading &&
                    (
                        <div className='p-4'>
                            <Title level={3}>
                                Welcome {buildUserFirstName()}
                            </Title>
                            <br />
                            <Text>
                                Please select a report from the side menu
                            </Text>
                            <div style={{ height: '70vh' }}></div>
                        </div>
                    )
                )
            }
            {
                (
                    currentError !== '' ||
                    isLoadFiltersError ||
                    isLoadDrilldownDataSetError ||
                    isLoadDrilldownChartError ||
                    isDownloadReportError ||
                    isManageDefaultReportError
                )
                &&
                (
                    <ServaAlert
                        className={'mb-2'}
                        message={determineWhichError()}
                        showIcon={true}
                        type="error"
                    ></ServaAlert>
                )
            }
            {
                isAreaLoading
                ?
                (
                    <Skeleton />
                )
                :
                (
                    allFiltersSet &&
                    okClickedOnFilterModal &&
                    (
                        <div className='ReportCanvas'>
                            <ParsedReportLayout
                                htmlContent={layout}
                                items={itemIdMap}
                                handleRowClick={handleRowClick}
                                handleBarClick={handleBarClick}
                            ></ParsedReportLayout>
                        </div>
                    )
                )
            }
        </>
    );
};

export default Home;
